import { Badge } from "@/app/components/ui/badge";
import { Button } from "@/app/components/ui/button.tsx";
import { Card, CardContent } from "@/app/components/ui/card";
import CompanyProfileSummaryCard from "@/app/Views/Companies/components/CompanyProfileSummaryCard";

const pricingTiers = [
	{
		name: "Starter Tier",
		price: "49k",
		bgColor: "bg-primary-700",
		textColor: "text-white",
		features: ["Verified Company Badge", "Auto-Updated Listings", "Full admin control", "Branded Employer Hub"],
	},
	{
		name: "Premier Tier",
		price: "89k",
		bgColor: "bg-[#ffffff]",
		textColor: "text-gray-900",
		features: ["Unlock website embedding", "Unlimited team seats"],
	},
	{
		name: "Enterprise Tier",
		price: "229k",
		bgColor: "bg-[#ffffff]",
		textColor: "text-gray-900",
		features: ["Get your own custom subdomain", "Advanced hiring insights & analytics"],
	},
];

const careerfyFeatures = ["A verified, branded employer hub.", "Organized, searchable, and official roles."];

const dprozFeatures = ["Official broadcast of all your jobs.", "Home of Tanzania's largest talent pool."];

export const HeroSection = ({ onClaimClick, companyName, isCompanyClaimed, companyDetails }) => {
	return (
		<section className="flex flex-col items-start w-full">
			<div className="w-full">
				<div className="flex h-[calc(100svh-73px)] min-h-[680px] w-full items-start gap-2.5 overflow-hidden pt-10 pb-0 px-4 md:px-8 lg:px-20 md:pt-14 [background:url(/images/claim-landing/frame-2121454035.png)_50%_50%/cover]">
					<div className="flex h-full flex-col w-full max-w-[1280px] mx-auto items-center">
						<div className="flex flex-col w-full max-w-[1039px] items-center gap-5 -translate-y-4 animate-fade-in opacity-0 md:gap-7">
							<h1 className="w-full max-w-[983px] font-['Plus_Jakarta_Sans',Helvetica] font-bold text-3xl md:text-5xl text-center tracking-normal leading-tight md:leading-[54px]">
								{!isCompanyClaimed && <span className="text-white">Is this </span>}
								<span className="text-anima-blue">
									{companyName} {`${isCompanyClaimed ? "" : "?"}`}
								</span>
								<br />
								<span className="text-white">Take command of your hiring brand.</span>
							</h1>

							{isCompanyClaimed ? (
								<p className="w-full font-['Inter',Helvetica] font-normal text-white/80 text-base md:text-xl text-center tracking-normal leading-7 md:leading-9">
									Manage your official employer page by verifying your corporate email in under 60 seconds.
								</p>
							) : (
								<p className="w-full font-['Inter',Helvetica] font-normal text-white/80 text-base md:text-xl text-center tracking-normal leading-7 md:leading-9">
									Claim your official employer page and verify your corporate email in under 60 seconds.
								</p>
							)}
						</div>

						<div className="mt-4 flex flex-col items-center gap-4 w-full -translate-y-4 animate-fade-in opacity-0 md:mt-6 md:gap-6 [--animation-delay:200ms]">
							<Button
								onClick={onClaimClick}
								className="h-auto text-gray-900 px-6 py-[11px] rounded-lg shadow-[0px_2px_8px_#0000001a] transition-transform"
								style={{ backgroundColor: "#f9bf29" }}
							>
								<span className="font-['Inter',Helvetica] font-bold text-base tracking-[-0.20px] leading-[27px]">
									{`${isCompanyClaimed ? "Login" : "Claim This Page"}`}
								</span>
							</Button>
						</div>

						<div className="mt-auto pt-4 w-full min-w-0 animate-fade-in opacity-0 [--animation-delay:320ms]">
							<CompanyProfileSummaryCard
								company={companyDetails}
								isActivelyHiring={true}
								showTabsShell={true}
								showJobsPreview={true}
								showBorder={false}
								cardPadding={true}
								className="mx-auto max-w-[97vw] sm:max-w-[820px] md:max-w-[980px] lg:max-w-[1100px] xl:max-w-[1180px] rounded-b-none"
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex flex-col lg:flex-row items-center justify-around gap-8 md:gap-16 lg:gap-[100px] pt-[80px] pb-20 px-4 md:px-8 lg:px-20 w-full bg-[#ffffff]">
				<div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16 flex-1 max-w-[1280px] mx-auto">
					<div className="gap-16 flex flex-col items-center flex-1">
						<div className="flex flex-col items-center gap-3 w-full">
							<h2 className="font-['Plus_Jakarta_Sans',Helvetica] font-bold text-anima-dark-text text-3xl md:text-4xl tracking-normal leading-[62.4px]">
								Stop Hiring in the Dark.
							</h2>
						</div>

						<div className="flex flex-col items-start justify-around gap-24 w-full">
							<div className="flex items-center gap-6 w-full">
								<Card
									className="flex-1 rounded-2xl overflow-hidden border-0 shadow-none bg-cover bg-center"
									style={{
										background: `
      linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.6) 0%,
        rgba(0, 0, 0, 0.6) 100%
      ),
      url('/images/claim-landing/content.png') center / cover no-repeat
    `,
									}}
								>
									<CardContent className="flex flex-col items-start gap-[252px] pt-6 pb-0 px-0">
										<div className="inline-flex flex-col items-start gap-2.5 px-6 py-0">
											<Badge className="h-auto bg-anima-red hover:bg-anima-red px-4 py-1.5 rounded-lg">
												<span className="font-['Inter',Helvetica] font-semibold text-anima-light-red-text text-sm text-center tracking-[0.02px] leading-4">
													The Reality
												</span>
											</Badge>
										</div>

										<div className="flex flex-col items-start justify-end gap-2.5 pt-8 pb-0 px-0 w-full rounded-[0px_0px_16px_16px] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_100%)]">
											<div className="flex flex-col items-center justify-center gap-6 p-6 w-full bg-[#ffffff4c] rounded-[0px_0px_16px_16px] backdrop-blur-md backdrop-brightness-100 [-webkit-backdrop-filter:blur(12px)_brightness(100%)]">
												<p className="w-full max-w-[362px] font-['Inter',Helvetica] font-semibold text-white text-xl text-center tracking-[-0.48px] leading-9">
													Scattered Posts &amp; PDF Overload
												</p>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card
									className="flex-1 rounded-2xl overflow-hidden border-0 shadow-none bg-cover bg-center"
									style={{
										background: `
      linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.6) 0%,
        rgba(0, 0, 0, 0.6) 100%
      ),
      url('/images/claim-landing/content-1.png') center / cover no-repeat
    `,
									}}
								>
									<CardContent className="flex flex-col items-start gap-16 px-0 py-6 h-full">
										<div className="flex flex-col items-start gap-2.5 px-6 py-0 w-full">
											<Badge className="h-auto bg-primary-700 hover:bg-primary-700 px-4 py-1.5 rounded-lg">
												<span className="font-['Inter',Helvetica] font-semibold text-primary-100 text-sm text-center tracking-[0.02px] leading-4">
													The Fix
												</span>
											</Badge>
										</div>

										<div className="flex flex-col items-start gap-4 px-6 py-0 flex-1 w-full">
											<div className="flex flex-col items-start gap-4 w-full">
												<Card className="w-full bg-[#ffffff] rounded-2xl border-0 shadow-none backdrop-blur-md backdrop-brightness-100 [-webkit-backdrop-filter:blur(12px)_brightness(100%)]">
													<CardContent className="p-0">
														<div className="flex flex-col items-start justify-center gap-2 p-4 w-full bg-primary-700 rounded-[16px_16px_0px_0px]">
															<div className="inline-flex items-center gap-2">
																<div className="inline-flex items-center gap-3 bg-anima-light-blue-bg rounded-lg">
																	<div className="inline-flex items-center gap-2.5 p-[7px] rounded-lg border border-solid border-anima-light-blue-bg">
																		<img className="w-4 h-4" alt="Table" src="/images/claim-landing/table.svg" />
																	</div>
																</div>

																<div className="inline-flex flex-col items-start justify-center gap-2">
																	<div className="inline-flex flex-col items-start gap-1">
																		<span className="font-['Inter',Helvetica] font-normal text-white text-base tracking-normal leading-6">
																			CareerfyHQ
																		</span>
																	</div>
																</div>
															</div>
														</div>

														<div className="flex flex-col items-start gap-4 pt-2 pb-4 px-4 w-full">
															<div className="flex flex-col items-start gap-2 w-full">
																{careerfyFeatures.map((feature, index) => (
																	<div key={index} className="flex items-start gap-2 w-full">
																		<img
																			className="w-4 h-4"
																			alt="CheckIcon icon"
																			src="/images/claim-landing/check-icon.svg"
																		/>
																		<div className="flex flex-col items-start flex-1">
																			<span className="font-['Inter',Helvetica] font-normal text-anima-gray-text-1 text-xs tracking-normal leading-[18px]">
																				{feature}
																			</span>
																		</div>
																	</div>
																))}
															</div>
														</div>
													</CardContent>
												</Card>

												<Card className="w-full bg-[#ffffff] rounded-2xl border-0 shadow-none backdrop-blur-md backdrop-brightness-100 [-webkit-backdrop-filter:blur(12px)_brightness(100%)]">
													<CardContent className="p-0">
														<div className="flex flex-col items-start justify-center gap-2 p-4 w-full bg-primary-700 rounded-[16px_16px_0px_0px]">
															<div className="inline-flex items-center gap-2">
																<div className="inline-flex items-center gap-3 bg-anima-light-blue-bg rounded-lg">
																	<div className="inline-flex items-center gap-2.5 p-[7px] rounded-lg border border-solid border-anima-light-blue-bg">
																		<img
																			className="w-4 h-4"
																			alt="Megaphone"
																			src="/images/claim-landing/megaphone.svg"
																		/>
																	</div>
																</div>

																<div className="inline-flex flex-col items-start justify-center gap-2">
																	<div className="inline-flex flex-col items-start gap-1">
																		<span className="font-['Inter',Helvetica] font-normal text-white text-base tracking-normal leading-6">
																			Dproz
																		</span>
																	</div>
																</div>
															</div>
														</div>

														<div className="flex flex-col items-start gap-4 pt-2 pb-4 px-4 w-full">
															<div className="flex flex-col items-start gap-2 w-full">
																{dprozFeatures.map((feature, index) => (
																	<div key={index} className="flex items-start w-full gap-3">
																		<img
																			className="w-4 h-4"
																			alt="CheckIcon icon"
																			src="/images/claim-landing/check-icon.svg"
																		/>
																		<div className="flex flex-col items-start flex-1">
																			<span className="font-['Inter',Helvetica] font-normal text-anima-gray-text-1 text-xs tracking-normal leading-[18px]">
																				{feature}
																			</span>
																		</div>
																	</div>
																))}
															</div>
														</div>
													</CardContent>
												</Card>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>

						<p className="w-full max-w-[791px] font-['Inter',Helvetica] font-normal text-anima-darker-gray-text text-base text-center tracking-normal leading-[27px]">
							Great companies lose great talent because of messy processes. Candidates are frustrated, and your HR team
							are overwhelmed.
						</p>
					</div>
				</div>
			</div>

			<div className="flex flex-col lg:flex-row items-center justify-around gap-8 md:gap-16 lg:gap-[100px] p-4 md:p-8 lg:p-20 w-full [background:url(/images/claim-landing/frame-2121454034.png)_50%_50%/cover,linear-gradient(0deg,rgba(239,246,255,0.5)_0%,rgba(239,246,255,0.5)_100%)]">
				<div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16 flex-1 max-w-[1280px] mx-auto">
					<div className="flex flex-col items-center gap-20 flex-1">
						<div className="flex flex-col items-center gap-3 w-full">
							<h2 className="font-['Plus_Jakarta_Sans',Helvetica] mb-16 font-bold text-anima-dark-text text-3xl md:text-4xl tracking-normal leading-[62.4px]">
								We built the ecosystem to solve this.
							</h2>

							<div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 w-full">
								<div className="flex flex-col md:flex-row items-stretch gap-4 md:gap-6 flex-1">
									{pricingTiers.map((tier, index) => (
										<Card
											key={index}
											className={`flex-1 flex flex-col ${
												index === 0 ? "shadow-[0px_2px_32px_rgb(10,111,226,0.4)]" : "shadow-none"
											} bg-[#ffffff] rounded-2xl border-0 overflow-hidden`}
										>
											<CardContent className="flex flex-col h-full p-0">
												<div
													className={`${tier.bgColor} rounded-[16px_16px_0px_0px] flex flex-col items-start gap-2 pt-6 pb-4 px-8`}
												>
													<div className="inline-flex flex-col items-start gap-1">
														<span
															className={`${tier.textColor} font-['Inter',Helvetica] font-normal text-base tracking-normal leading-[27px]`}
														>
															{tier.name}
														</span>
													</div>

													<div className="inline-flex flex-col items-start gap-0.5">
														<span
															className={`${tier.textColor} font-['Inter',Helvetica] font-normal text-xl tracking-normal leading-5`}
														>
															<span className="text-lg leading-[30px]">TZS</span>
															<span className="text-sm leading-6">&nbsp;</span>
															<span className="font-medium text-[32px] leading-[48px]">{tier.price}</span>
															<span className="text-sm leading-6">&nbsp;</span>
															<span className="text-xs leading-[18px]">/month</span>
														</span>
													</div>
												</div>

												<div className="flex flex-col items-start gap-4 pt-4 pb-6 px-8 flex-1 overflow-hidden">
													<div className="gap-4 inline-flex flex-col items-start h-full">
														<div className="inline-flex flex-col items-start gap-2 h-full overflow-auto">
															{tier.features.map((feature, featureIndex) => (
																<div key={featureIndex} className="flex items-center w-full gap-3">
																	<img
																		className="w-5 h-5"
																		alt="CheckIcon icon"
																		src="/images/claim-landing/check-icon-2.svg"
																	/>
																	<div className="inline-flex flex-col items-start">
																		<span className="font-['Inter',Helvetica] font-normal text-anima-gray-text-1 text-sm tracking-normal leading-6">
																			{feature}
																		</span>
																	</div>
																</div>
															))}
														</div>
													</div>
												</div>
											</CardContent>
										</Card>
									))}
								</div>
							</div>

							<div className="inline-flex items-center justify-center gap-2.5 pt-4 pb-0 px-0">
								<p className="w-full max-w-[790px] font-['Inter',Helvetica] mt-16 font-normal text-anima-darker-gray-text text-base text-center tracking-normal leading-[27px]">
									Candidates find you instantly. You stay visible. You never miss a great hire again.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
